import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

interface TradeNotification {
  type: 'trade_update' | 'webhook_received' | 'connected' | 'pending_trade';
  timestamp: string;
  data?: {
    trade_id?: number;
    account_id?: number;
    account_name?: string;
    symbol: string;
    status?: string;
    transaction_type?: string;
    quantity?: number;
    order_id?: string;
    error_message?: string;
    // Pending trade fields
    pending_trade_id?: number;
    action?: string;
    accounts_count?: number;
    current_holdings?: { [key: string]: number };
    is_increasing_exposure?: boolean;
  };
}

export const useNotifications = (userId?: number) => {
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    // Don't connect if no user ID
    if (!userId && userId !== 0) return;

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    const url = `${API_URL}/notifications/stream?user_id=${userId}`;

    // Create EventSource connection
    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      console.log('✅ SSE connected');
      setIsConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const notification: TradeNotification = JSON.parse(event.data);
        console.log('📨 Received notification:', notification);

        handleNotification(notification);
      } catch (error) {
        console.error('Failed to parse notification:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('❌ SSE error:', error);
      setIsConnected(false);

      // EventSource will automatically try to reconnect
    };

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        setIsConnected(false);
      }
    };
  }, [userId]);

  const handleNotification = (notification: TradeNotification) => {
    if (notification.type === 'connected') {
      console.log('Connected to notification stream');
      return;
    }

    if (notification.type === 'pending_trade' && notification.data) {
      const { symbol, action, quantity, accounts_count, pending_trade_id, current_holdings, is_increasing_exposure } = notification.data;

      // Calculate total current shares if holdings exist
      const totalCurrentShares = current_holdings
        ? Object.values(current_holdings).reduce((sum: number, qty: any) => sum + qty, 0)
        : 0;

      // Pending trade notification with click to view
      toast(
        (t) => (
          <div className="cursor-pointer" onClick={() => {
            window.location.href = '/pending';
            toast.dismiss(t.id);
          }}>
            <div className="font-bold text-yellow-900">
              {is_increasing_exposure ? '⬆️ Increasing Exposure' : '⏳ Trade Requires Approval'}
            </div>
            <div className="text-yellow-800 mt-1">
              {action} {quantity} {symbol} on {accounts_count} account(s)
            </div>
            {is_increasing_exposure && totalCurrentShares > 0 && quantity && (
              <div className="text-xs text-orange-700 mt-1 font-semibold">
                Currently holding {totalCurrentShares} shares → will become {totalCurrentShares + quantity} shares
              </div>
            )}
            <div className="text-xs text-yellow-700 mt-2">
              Click to review and approve →
            </div>
          </div>
        ),
        {
          duration: 10000,
          icon: is_increasing_exposure ? '⬆️' : '⚠️',
          style: {
            background: is_increasing_exposure ? '#FED7AA' : '#FEF3C7',
            color: '#92400E',
            border: is_increasing_exposure ? '2px solid #F97316' : '2px solid #F59E0B',
            cursor: 'pointer',
          },
        }
      );
      return;
    }

    if (notification.type === 'trade_update' && notification.data) {
      const { status, symbol, account_name, transaction_type, quantity, error_message } = notification.data;

      if (status === 'SUCCESS') {
        // Success notification
        toast.success(
          `✅ ${transaction_type} Order Placed\n${symbol} x${quantity} on ${account_name}`,
          {
            duration: 5000,
            icon: '🎯',
            style: {
              background: '#10B981',
              color: '#fff',
            },
          }
        );
      } else if (status === 'FAILED') {
        // Failure notification - using string instead of JSX
        const errorMsg = error_message
          ? `❌ Order Failed\n${symbol} x${quantity} on ${account_name}\n\n${error_message}`
          : `❌ Order Failed\n${symbol} x${quantity} on ${account_name}`;

        toast.error(
          errorMsg,
          {
            duration: 8000,
            style: {
              background: '#EF4444',
              color: '#fff',
              maxWidth: '500px',
              whiteSpace: 'pre-line',
            },
          }
        );
      }
    }

    if (notification.type === 'webhook_received' && notification.data) {
      // Webhook received notification (optional)
      toast(
        `📥 Webhook received: ${notification.data.symbol}`,
        {
          duration: 3000,
          icon: '📡',
        }
      );
    }
  };

  return {
    isConnected,
  };
};
